import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { NAV_CATEGORIES, timeAgo } from "@/lib/format";
import { SearchOverlay } from "@/components/public/SearchOverlay";
import api from "@/lib/api";

function BriefTicker() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/homepage").then((r) => setItems(r.data.brief || [])).catch(() => {});
  }, []);
  if (!items.length) return null;
  const doubled = [...items, ...items];
  return (
    <div className="bg-ink text-paper overflow-hidden" data-testid="brief-ticker">
      <div className="flex items-center">
        <div className="shrink-0 bg-crimson px-3 py-1.5 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse-dot" />
          <span className="text-[10px] font-bold uppercase tracking-[0.15em]">The Fin Desk Brief</span>
        </div>
        <div className="relative flex-1 overflow-hidden">
          <div className="flex gap-8 whitespace-nowrap animate-marquee py-1.5 w-max">
            {doubled.map((it, i) => (
              <Link key={i} to={`/${it.category}/${it.slug}`} className="text-xs font-mono text-paper/80 hover:text-paper flex items-center gap-2">
                <span className="text-crimson">▸</span>{it.headline}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PublicLayout({ children }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <BriefTicker />

      {/* Masthead */}
      <div className="border-b border-hair">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="hidden sm:flex justify-between items-center py-2 text-[11px] text-mutedink font-medium border-b border-hair/60">
            <span className="font-mono">{today}</span>
            <span className="uppercase tracking-[0.15em]">Finance · Fintech · Payments · Intelligence</span>
            <span className="uppercase tracking-[0.15em] text-mutedink/70">Est. 2026</span>
          </div>
          <div className="flex items-center justify-center py-5 sm:py-7 relative">
            <button className="sm:hidden absolute left-0" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open"><Menu size={22} /></button>
            <Link to="/" data-testid="masthead-logo" className="text-center">
              <div className="font-serif font-bold tracking-tight text-3xl sm:text-5xl text-ink leading-none">THE FIN DESK</div>
            </Link>
            <button className="absolute right-0 hover:text-crimson transition-colors" onClick={() => setSearchOpen(true)} data-testid="search-open-button">
              <Search size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Primary nav */}
      <nav className={`sticky top-0 z-40 border-b border-hair transition-all ${scrolled ? "bg-paper/90 backdrop-blur-md" : "bg-paper"}`}>
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="hidden sm:flex items-center justify-center gap-6 lg:gap-8 py-3 overflow-x-auto no-scrollbar">
            <NavLink to="/" end className={({isActive}) => `text-xs font-semibold uppercase tracking-[0.13em] whitespace-nowrap transition-colors ${isActive ? "text-crimson" : "text-ink hover:text-crimson"}`} data-testid="nav-link-home">Home</NavLink>
            <NavLink to="/latest" className={({isActive}) => `text-xs font-semibold uppercase tracking-[0.13em] whitespace-nowrap transition-colors ${isActive ? "text-crimson" : "text-ink hover:text-crimson"}`} data-testid="nav-link-latest">Latest</NavLink>
            {NAV_CATEGORIES.map((c) => (
              <NavLink key={c.slug} to={`/${c.slug}`} className={({isActive}) => `text-xs font-semibold uppercase tracking-[0.13em] whitespace-nowrap transition-colors ${isActive ? "text-crimson" : "text-ink hover:text-crimson"}`} data-testid={`nav-link-${c.slug}`}>{c.label}</NavLink>
            ))}
            <NavLink to="/companies" className={({isActive}) => `text-xs font-semibold uppercase tracking-[0.13em] whitespace-nowrap transition-colors ${isActive ? "text-crimson" : "text-ink hover:text-crimson"}`} data-testid="nav-link-companies">Companies</NavLink>
            <NavLink to="/insights" className={({isActive}) => `text-xs font-semibold uppercase tracking-[0.13em] whitespace-nowrap transition-colors ${isActive ? "text-crimson" : "text-ink hover:text-crimson"}`} data-testid="nav-link-insights">Insights</NavLink>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[90] bg-ink/40" onClick={() => setMobileOpen(false)}>
          <div className="bg-paper w-72 h-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <span className="font-serif font-bold text-2xl">TFD</span>
              <button onClick={() => setMobileOpen(false)}><X size={20} /></button>
            </div>
            <div className="flex flex-col gap-1">
              {[{slug:"",label:"Home"},{slug:"latest",label:"Latest"},...NAV_CATEGORIES,{slug:"companies",label:"Companies"},{slug:"insights",label:"Insights"}].map((c) => (
                <Link key={c.slug} to={`/${c.slug}`} onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-semibold uppercase tracking-wider text-ink border-b border-hair">{c.label}</Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-ink text-paper mt-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-14">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2">
              <div className="font-serif font-bold text-3xl">THE FIN DESK</div>
              <p className="mt-2 text-sm text-paper/60 max-w-xs">Finance. Fintech. Payments. Intelligence. Independent reporting on the infrastructure behind modern finance.</p>
            </div>
            <FooterCol title="Sections" links={[["Fintech","/fintech"],["Payments","/payments"],["Banking","/banking"],["Infrastructure","/infrastructure"],["Regulation","/regulation"]]} />
            <FooterCol title="The Desk" links={[["About","/about"],["Authors","/authors"],["Newsletter","/newsletter"],["Advertise","/advertise"],["Contact","/contact"]]} />
            <FooterCol title="Policies" links={[["Editorial Policy","/editorial-policy"],["Corrections","/corrections-policy"],["Privacy","/privacy-policy"],["Terms","/terms"]]} />
          </div>
          <div className="mt-12 pt-6 border-t border-paper/15 flex flex-col sm:flex-row justify-between text-xs text-paper/50 gap-2">
            <span>© {new Date().getFullYear()} The Fin Desk. Independent digital publication.</span>
            <div className="flex items-center gap-4">
              <a href="/api/rss.xml" target="_blank" rel="noreferrer" className="font-mono hover:text-paper transition-colors" data-testid="footer-rss">RSS</a>
              <span className="font-mono">thefindesk.com</span>
            </div>
          </div>
        </div>
      </footer>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-crimson mb-3">{title}</div>
      <div className="flex flex-col gap-2">
        {links.map(([l, to]) => (
          <Link key={to} to={to} className="text-sm text-paper/70 hover:text-paper transition-colors">{l}</Link>
        ))}
      </div>
    </div>
  );
}
